import type { ConnectorField, ConnectorSchema } from "./schema";

function personCore(overrides?: {
  emailKey?: string;
  firstNameKey?: string;
  lastNameKey?: string;
  emailAliases?: string[];
}): ConnectorField[] {
  const emailKey = overrides?.emailKey ?? "email";
  const firstNameKey = overrides?.firstNameKey ?? "firstName";
  const lastNameKey = overrides?.lastNameKey ?? "lastName";
  return [
    {
      key: firstNameKey,
      label: "First Name",
      type: "string",
      required: true,
      writable: true,
      aliases: [
        "first_name",
        "firstname",
        "given_name",
        ...(overrides?.firstNameKey ? [] : []),
      ],
    },
    {
      key: lastNameKey,
      label: "Last Name",
      type: "string",
      required: true,
      writable: true,
      aliases: ["last_name", "lastname", "surname", "family_name"],
    },
    {
      key: emailKey,
      label: "Email",
      type: "email",
      required: true,
      writable: true,
      aliases: [
        "email",
        "email_address",
        "email1",
        "primary_email",
        ...(overrides?.emailAliases ?? []),
      ],
    },
    {
      key: "phone",
      label: "Phone",
      type: "phone",
      writable: true,
      aliases: ["phone_number", "mobile", "telephone"],
    },
    {
      key: "company",
      label: "Company",
      type: "string",
      writable: true,
      aliases: [
        "organization",
        "organisation",
        "company_name",
        "account_name",
        "org",
      ],
    },
    {
      key: "jobTitle",
      label: "Job Title",
      type: "string",
      writable: true,
      aliases: ["job_title", "title", "position"],
    },
    {
      key: "address",
      label: "Address",
      type: "string",
      writable: true,
      aliases: ["street", "street_address", "mailing_street"],
    },
    {
      key: "city",
      label: "City",
      type: "string",
      writable: true,
      aliases: ["town", "mailing_city"],
    },
    {
      key: "state",
      label: "State / Province",
      type: "string",
      writable: true,
      aliases: ["province", "region", "mailing_state"],
    },
    {
      key: "zip",
      label: "Postal Code",
      type: "string",
      writable: true,
      aliases: ["postal_code", "zip_code", "mailing_postal"],
    },
    {
      key: "country",
      label: "Country",
      type: "string",
      writable: true,
      aliases: ["country_code", "mailing_country"],
    },
    {
      key: "industry",
      label: "Industry",
      type: "string",
      writable: true,
      aliases: ["sector"],
    },
    {
      key: "comments",
      label: "Comments",
      type: "string",
      writable: true,
      aliases: ["notes", "comment", "description"],
    },
    {
      key: "employeeCount",
      label: "Number of Employees",
      type: "string",
      writable: true,
      aliases: ["no_of_employees", "number_of_employees", "employees"],
    },
    {
      key: "purchasingTimeFrame",
      label: "Purchasing Time Frame",
      type: "string",
      writable: true,
      aliases: ["purchasing_time_frame"],
    },
    {
      key: "purchaseRole",
      label: "Role in Purchase Process",
      type: "string",
      writable: true,
      aliases: ["role_in_purchase_process", "buying_role"],
    },
    {
      key: "registeredAt",
      label: "Registration Date",
      type: "datetime",
      writable: true,
      aliases: ["create_time", "registration_date", "registered_at"],
    },
    {
      key: "eventId",
      label: "Event ID",
      type: "string",
      writable: true,
      aliases: ["meeting_id", "crm_event_id", "campaign_id"],
    },
    {
      key: "eventName",
      label: "Event Name",
      type: "string",
      writable: true,
      aliases: ["meeting_topic", "event_title", "campaign_name"],
    },
    {
      key: "attendanceStart",
      label: "Attendance Start",
      type: "datetime",
      writable: true,
      aliases: ["join_time", "check_in", "attended_at"],
    },
    {
      key: "attendanceEnd",
      label: "Attendance End",
      type: "datetime",
      writable: true,
      aliases: ["leave_time", "check_out"],
    },
    {
      key: "attendanceDuration",
      label: "Attendance Duration",
      type: "number",
      writable: true,
      aliases: ["duration", "minutes_attended"],
    },
    {
      key: "recordId",
      label: "Record ID",
      type: "string",
      writable: true,
      description: "Existing CRM record identifier when matching by ID",
      aliases: ["account_id", "contact_id", "destination_id", "crm_id"],
    },
  ];
}

/** Standard Zoom meeting/webinar registrant fields available to sync. */
const ZOOM_FIELDS: ConnectorField[] = [
  {
    key: "firstName",
    label: "First Name",
    type: "string",
    readable: true,
    aliases: ["first_name", "firstname"],
  },
  {
    key: "lastName",
    label: "Last Name",
    type: "string",
    readable: true,
    aliases: ["last_name", "lastname"],
  },
  {
    key: "email",
    label: "Email",
    type: "email",
    readable: true,
    aliases: ["email_address"],
  },
  {
    key: "phone",
    label: "Phone",
    type: "phone",
    readable: true,
    aliases: ["phone_number"],
  },
  {
    key: "company",
    label: "Organization",
    type: "string",
    readable: true,
    description: "Zoom registrant org field",
    aliases: ["org", "organization"],
  },
  {
    key: "jobTitle",
    label: "Job Title",
    type: "string",
    readable: true,
    aliases: ["job_title", "title"],
  },
  {
    key: "address",
    label: "Address",
    type: "string",
    readable: true,
    aliases: ["street"],
  },
  {
    key: "city",
    label: "City",
    type: "string",
    readable: true,
  },
  {
    key: "state",
    label: "State / Province",
    type: "string",
    readable: true,
    aliases: ["province"],
  },
  {
    key: "zip",
    label: "Postal Code",
    type: "string",
    readable: true,
    aliases: ["postal_code", "zip_code"],
  },
  {
    key: "country",
    label: "Country",
    type: "string",
    readable: true,
  },
  {
    key: "industry",
    label: "Industry",
    type: "string",
    readable: true,
  },
  {
    key: "comments",
    label: "Comments",
    type: "string",
    readable: true,
    aliases: ["notes"],
  },
  {
    key: "employeeCount",
    label: "Number of Employees",
    type: "string",
    readable: true,
    aliases: ["no_of_employees"],
  },
  {
    key: "purchasingTimeFrame",
    label: "Purchasing Time Frame",
    type: "string",
    readable: true,
    aliases: ["purchasing_time_frame"],
  },
  {
    key: "purchaseRole",
    label: "Role in Purchase Process",
    type: "string",
    readable: true,
    aliases: ["role_in_purchase_process"],
  },
  {
    key: "registeredAt",
    label: "Registered At",
    type: "datetime",
    readable: true,
    aliases: ["create_time", "registered_at"],
  },
  {
    key: "status",
    label: "Registration Status",
    type: "string",
    readable: true,
    aliases: ["registration_status"],
  },
  {
    key: "id",
    label: "Registrant ID",
    type: "string",
    readable: true,
    aliases: ["participant_uuid", "participant_id", "registrant_id"],
  },
  {
    key: "attended",
    label: "Attended",
    type: "boolean",
    readable: true,
    description:
      "True when Zoom past-participant reports include this registrant (requires syncAttendance + past-participant scopes)",
    aliases: ["attendance", "did_attend"],
  },
  {
    key: "attendanceStart",
    label: "Attendance Start",
    type: "datetime",
    readable: true,
    aliases: ["join_time", "check_in"],
  },
  {
    key: "attendanceEnd",
    label: "Attendance End",
    type: "datetime",
    readable: true,
    aliases: ["leave_time", "check_out"],
  },
  {
    key: "attendanceDuration",
    label: "Attendance Duration (Minutes)",
    type: "number",
    readable: true,
    aliases: ["duration", "minutes_attended"],
  },
];

const ZOOM_RECOMMENDED = [
  { sourceKey: "firstName", destinationKey: "firstName" },
  { sourceKey: "lastName", destinationKey: "lastName" },
  { sourceKey: "email", destinationKey: "email" },
  { sourceKey: "phone", destinationKey: "phone" },
  { sourceKey: "company", destinationKey: "company" },
  { sourceKey: "jobTitle", destinationKey: "jobTitle" },
  { sourceKey: "address", destinationKey: "address" },
  { sourceKey: "city", destinationKey: "city" },
  { sourceKey: "state", destinationKey: "state" },
  { sourceKey: "zip", destinationKey: "zip" },
  { sourceKey: "country", destinationKey: "country" },
  { sourceKey: "industry", destinationKey: "industry" },
  { sourceKey: "comments", destinationKey: "comments" },
  { sourceKey: "employeeCount", destinationKey: "employeeCount" },
  { sourceKey: "purchasingTimeFrame", destinationKey: "purchasingTimeFrame" },
  { sourceKey: "purchaseRole", destinationKey: "purchaseRole" },
  { sourceKey: "registeredAt", destinationKey: "registeredAt" },
];

function destinationSchema(
  provider: string,
  label: string,
  fields: ConnectorField[],
  recommendedMappings = ZOOM_RECOMMENDED,
): ConnectorSchema {
  return {
    provider,
    label,
    kind: "destination",
    capabilities: {
      supportsEventTriggeredSync: true,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields,
    recommendedMappings,
  };
}

function remapRecommended(
  emailKey: string,
  firstNameKey: string,
  lastNameKey: string,
): Array<{ sourceKey: string; destinationKey: string }> {
  return ZOOM_RECOMMENDED.map((row) => {
    if (row.sourceKey === "firstName")
      return { ...row, destinationKey: firstNameKey };
    if (row.sourceKey === "lastName")
      return { ...row, destinationKey: lastNameKey };
    if (row.sourceKey === "email") return { ...row, destinationKey: emailKey };
    return row;
  });
}

const SOURCE_SCHEMAS: Record<string, ConnectorSchema> = {
  zoom: {
    provider: "zoom",
    label: "Zoom",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: true,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: ZOOM_FIELDS,
  },
  google_meet: {
    provider: "google_meet",
    label: "Google Meet",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "status",
        label: "RSVP Status",
        type: "string",
        readable: true,
        description:
          "Calendar attendee responseStatus (accepted, tentative, needsAction, declined)",
        aliases: ["response_status", "rsvp"],
      },
      {
        key: "registeredAt",
        label: "Invited At",
        type: "datetime",
        readable: true,
        aliases: ["invited_at"],
      },
    ],
  },
  google_forms: {
    provider: "google_forms",
    label: "Google Forms",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address", "respondent_email"],
      },
      {
        key: "status",
        label: "Response Status",
        type: "string",
        readable: true,
        description: "Form submission status (submitted)",
        aliases: ["response_status"],
      },
      {
        key: "registeredAt",
        label: "Submitted At",
        type: "datetime",
        readable: true,
        aliases: ["submitted_at", "last_submitted_time"],
      },
    ],
  },
  linkedin_events: {
    provider: "linkedin_events",
    label: "LinkedIn Events",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: true,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name", "FIRST_NAME"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name", "LAST_NAME"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address", "EMAIL", "WORK_EMAIL"],
      },
      {
        key: "jobTitle",
        label: "Job Title",
        type: "string",
        readable: true,
        aliases: ["job_title", "title", "JOB_TITLE"],
      },
      {
        key: "company",
        label: "Company",
        type: "string",
        readable: true,
        aliases: ["organization", "company_name", "COMPANY_NAME"],
      },
      {
        key: "country",
        label: "Country / Region",
        type: "string",
        readable: true,
        aliases: ["country_region", "COUNTRY"],
      },
      {
        key: "registeredAt",
        label: "Registered At",
        type: "datetime",
        readable: true,
        aliases: ["submitted_at", "registered_at"],
      },
      {
        key: "status",
        label: "Registration Status",
        type: "string",
        readable: true,
        aliases: ["lead_status"],
      },
    ],
  },
  constant_contact: {
    provider: "constant_contact",
    label: "Constant Contact",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "registeredAt",
        label: "Registered At",
        type: "datetime",
        readable: true,
        aliases: ["registration_time", "registration_date", "registered_at"],
      },
      {
        key: "status",
        label: "Registration Status",
        type: "string",
        readable: true,
        aliases: ["registration_status"],
      },
    ],
  },
  heysummit: {
    provider: "heysummit",
    label: "HeySummit",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: true,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name", "name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "phone",
        label: "Phone",
        type: "phone",
        readable: true,
        aliases: ["phone_number", "mobile"],
      },
      {
        key: "company",
        label: "Company",
        type: "string",
        readable: true,
        aliases: ["organisation", "organization", "org"],
      },
      {
        key: "jobTitle",
        label: "Job Title",
        type: "string",
        readable: true,
        aliases: ["job_title", "title"],
      },
      {
        key: "registeredAt",
        label: "Registered At",
        type: "datetime",
        readable: true,
        aliases: ["created", "registered_at", "date_created"],
      },
      {
        key: "status",
        label: "Registration Status",
        type: "string",
        readable: true,
        aliases: ["attendee_status"],
      },
    ],
  },
  fluent_forms: {
    provider: "fluent_forms",
    label: "Fluent Forms",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: true,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name", "names.first_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name", "names.last_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address", "your_email"],
      },
      {
        key: "phone",
        label: "Phone",
        type: "phone",
        readable: true,
        aliases: ["phone_number", "mobile", "tel"],
      },
      {
        key: "company",
        label: "Company",
        type: "string",
        readable: true,
        aliases: ["organisation", "organization", "org", "company_name"],
      },
      {
        key: "jobTitle",
        label: "Job Title",
        type: "string",
        readable: true,
        aliases: ["job_title", "title"],
      },
      {
        key: "registeredAt",
        label: "Submitted At",
        type: "datetime",
        readable: true,
        aliases: ["created_at", "submitted_at", "registered_at"],
      },
      {
        key: "status",
        label: "Submission Status",
        type: "string",
        readable: true,
        aliases: ["entry_status"],
      },
    ],
  },
  eventbrite: {
    provider: "eventbrite",
    label: "Eventbrite",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "phone",
        label: "Phone",
        type: "phone",
        readable: true,
        aliases: ["cell_phone", "work_phone", "phone_number"],
      },
      {
        key: "company",
        label: "Company",
        type: "string",
        readable: true,
        aliases: ["organisation", "organization", "org"],
      },
      {
        key: "jobTitle",
        label: "Job Title",
        type: "string",
        readable: true,
        aliases: ["job_title", "title"],
      },
      {
        key: "address",
        label: "Address",
        type: "string",
        readable: true,
        aliases: ["address_1", "street"],
      },
      {
        key: "city",
        label: "City",
        type: "string",
        readable: true,
      },
      {
        key: "state",
        label: "State / Province",
        type: "string",
        readable: true,
        aliases: ["region", "province"],
      },
      {
        key: "zip",
        label: "Postal Code",
        type: "string",
        readable: true,
        aliases: ["postal_code", "zip_code"],
      },
      {
        key: "country",
        label: "Country",
        type: "string",
        readable: true,
      },
      {
        key: "registeredAt",
        label: "Registered At",
        type: "datetime",
        readable: true,
        aliases: ["created", "registration_date", "registered_at"],
      },
      {
        key: "status",
        label: "Attendee Status",
        type: "string",
        readable: true,
      },
    ],
  },
  microsoft_teams: {
    provider: "microsoft_teams",
    label: "Microsoft Teams",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "registeredAt",
        label: "Registered At",
        type: "datetime",
        readable: true,
        aliases: ["registrationDateTime", "registration_date", "registered_at"],
      },
      {
        key: "status",
        label: "Registration Status",
        type: "string",
        readable: true,
      },
    ],
  },
  mailchimp: {
    provider: "mailchimp",
    label: "Mailchimp",
    kind: "source",
    capabilities: {
      supportsEventTriggeredSync: false,
      supportsScheduledSync: true,
      supportsSchemaDiscovery: true,
    },
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "string",
        readable: true,
        aliases: ["first_name", "given_name", "FNAME"],
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "string",
        readable: true,
        aliases: ["last_name", "family_name", "LNAME"],
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        readable: true,
        aliases: ["email_address"],
      },
      {
        key: "phone",
        label: "Phone",
        type: "phone",
        readable: true,
        aliases: ["phone_number", "PHONE"],
      },
      {
        key: "address",
        label: "Address",
        type: "string",
        readable: true,
        aliases: ["addr1", "street"],
      },
      {
        key: "city",
        label: "City",
        type: "string",
        readable: true,
        aliases: ["city_name"],
      },
      {
        key: "state",
        label: "State / Province",
        type: "string",
        readable: true,
        aliases: ["province", "region"],
      },
      {
        key: "zip",
        label: "Postal Code",
        type: "string",
        readable: true,
        aliases: ["postal_code", "zip_code"],
      },
      {
        key: "country",
        label: "Country",
        type: "string",
        readable: true,
        aliases: ["country_code"],
      },
      {
        key: "registeredAt",
        label: "Signed Up At",
        type: "datetime",
        readable: true,
        aliases: ["timestamp_signup", "timestamp_opt", "registered_at"],
      },
      {
        key: "status",
        label: "Subscription Status",
        type: "string",
        readable: true,
        description: "Mailchimp member status (subscribed)",
        aliases: ["member_status"],
      },
    ],
  },
};

const DESTINATION_SCHEMAS: Record<string, ConnectorSchema> = {
  neon: destinationSchema(
    "neon",
    "Neon CRM",
    personCore({ emailKey: "email", emailAliases: ["email1"] }).map((f) =>
      f.key === "company"
        ? {
            ...f,
            label: "Company Name",
            aliases: [...(f.aliases ?? []), "companyName"],
          }
        : f,
    ),
  ),
  salesforce: destinationSchema(
    "salesforce",
    "Salesforce",
    // Standard Contact has no Industry (Account does) — omit from Contact mapping catalog.
    personCore({
      firstNameKey: "FirstName",
      lastNameKey: "LastName",
      emailKey: "Email",
    }).filter((f) => f.key !== "industry"),
    remapRecommended("Email", "FirstName", "LastName").filter(
      (row) => row.sourceKey !== "industry",
    ),
  ),
  hubspot: destinationSchema(
    "hubspot",
    "HubSpot",
    // Contact upsert writes these standard properties (+ live-discovered customs via mappings).
    // Attendance / event / purchase-role fields are not written by the adapter.
    personCore({
      firstNameKey: "firstname",
      lastNameKey: "lastname",
      emailKey: "email",
    })
      .map((f) => {
        if (f.key === "jobTitle")
          return {
            ...f,
            key: "jobtitle",
            aliases: [...(f.aliases ?? []), "jobTitle"],
          };
        return f;
      })
      .filter((f) =>
        [
          "firstname",
          "lastname",
          "email",
          "phone",
          "company",
          "jobtitle",
          "address",
          "city",
          "state",
          "zip",
          "country",
          "industry",
        ].includes(f.key),
      ),
    remapRecommended("email", "firstname", "lastname")
      .map((row) =>
        row.sourceKey === "jobTitle"
          ? { sourceKey: "jobTitle", destinationKey: "jobtitle" }
          : row,
      )
      .filter((row) =>
        [
          "email",
          "firstName",
          "lastName",
          "phone",
          "company",
          "jobTitle",
          "address",
          "city",
          "state",
          "zip",
          "country",
          "industry",
        ].includes(row.sourceKey),
      ),
  ),
  zoho: destinationSchema(
    "zoho",
    "Zoho CRM",
    // Contact upsert + Campaign relation — no attendance / event name / purchase fields.
    personCore({
      firstNameKey: "First_Name",
      lastNameKey: "Last_Name",
      emailKey: "Email",
    }).filter((f) =>
      [
        "First_Name",
        "Last_Name",
        "Email",
        "phone",
        "company",
        "jobTitle",
        "address",
        "city",
        "state",
        "zip",
        "country",
        "industry",
        "comments",
      ].includes(f.key),
    ),
    remapRecommended("Email", "First_Name", "Last_Name").filter((row) =>
      [
        "email",
        "firstName",
        "lastName",
        "phone",
        "company",
        "jobTitle",
        "address",
        "city",
        "state",
        "zip",
        "country",
        "industry",
        "comments",
      ].includes(row.sourceKey),
    ),
  ),
  dynamics: destinationSchema(
    "dynamics",
    "Dynamics 365",
    personCore({
      firstNameKey: "firstname",
      lastNameKey: "lastname",
      emailKey: "emailaddress1",
    }),
    remapRecommended("emailaddress1", "firstname", "lastname"),
  ),
  // Person upsert: name/email/phone + org_id from company. Event = completed Activity only.
  pipedrive: destinationSchema(
    "pipedrive",
    "Pipedrive",
    personCore().filter((f) =>
      ["firstName", "lastName", "email", "phone", "company"].includes(f.key),
    ),
    ZOOM_RECOMMENDED.filter((row) =>
      ["firstName", "lastName", "email", "phone", "company"].includes(
        row.sourceKey,
      ),
    ),
  ),
  blackbaud: destinationSchema("blackbaud", "Blackbaud RE NXT", personCore()),
  civicrm: destinationSchema(
    "civicrm",
    "CiviCRM",
    personCore({
      firstNameKey: "first_name",
      lastNameKey: "last_name",
      emailKey: "email",
    }),
    remapRecommended("email", "first_name", "last_name"),
  ),
  bloomerang: destinationSchema(
    "bloomerang",
    "Bloomerang",
    personCore({
      firstNameKey: "FirstName",
      lastNameKey: "LastName",
      emailKey: "PrimaryEmail",
    }),
    remapRecommended("PrimaryEmail", "FirstName", "LastName"),
  ),
  donorperfect: destinationSchema("donorperfect", "DonorPerfect", personCore()),
  everyaction: destinationSchema("everyaction", "EveryAction", personCore()),
  nationbuilder: destinationSchema(
    "nationbuilder",
    "NationBuilder",
    personCore({
      firstNameKey: "first_name",
      lastNameKey: "last_name",
      emailKey: "email",
    }),
    remapRecommended("email", "first_name", "last_name"),
  ),
  // Demo/testing-only: mirrors a standard person catalog; writes nothing.
  mock_crm: destinationSchema("mock_crm", "Mock CRM (Demo)", personCore()),
};

/** Resolve a connector field schema by provider id (source or destination). */
export function getConnectorFieldSchema(
  provider: string,
): ConnectorSchema | null {
  const key = provider.trim().toLowerCase();
  return SOURCE_SCHEMAS[key] ?? DESTINATION_SCHEMAS[key] ?? null;
}

export function listConnectorFieldSchemas(): ConnectorSchema[] {
  return [
    ...Object.values(SOURCE_SCHEMAS),
    ...Object.values(DESTINATION_SCHEMAS),
  ];
}

export function getSourceFieldSchema(provider: string): ConnectorSchema | null {
  const schema = getConnectorFieldSchema(provider);
  return schema?.kind === "source" ? schema : null;
}

export function getDestinationFieldSchema(
  provider: string,
): ConnectorSchema | null {
  const schema = getConnectorFieldSchema(provider);
  return schema?.kind === "destination" ? schema : null;
}
